import { useState, useEffect, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import type { Quotation, Party, SalesRep, ItemCode } from '../../types';
import { useCompany } from '../../context/CompanyContext';
import { Button } from '../../components/common/Button';
import { generateQuotationPDF } from '../../lib/pdfGenerator';
import { generateQuotationExcel } from '../../lib/excelGenerator';
import { calculateFinancials, calculateLineItem, type LineItemInput } from '../../lib/pricing';
import { logUserActivity } from '../../lib/activityLogger';

interface QuotationsPageProps {
  onEditQuotation?: (id: number) => void;
}

export default function QuotationsPage({ onEditQuotation }: QuotationsPageProps) {
  const {
    companies,
    selectedCompanyId,
    selectedCompany,
    setSelectedCompanyId,
  } = useCompany();
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [reps, setReps] = useState<SalesRep[]>([]);
  const [catalogItems, setCatalogItems] = useState<ItemCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [form, setForm] = useState({
    quotation_number: '',
    quotation_date: new Date().toISOString().split('T')[0],
    company_id: '',
    rep_id: '',
    party_id: '',
    query_number: '',
    query_date: '',
    kind_attn: '',
    status: 'draft' as Quotation['status'],
    global_discount_percent: 0,
    pf_percent: 0,
    others: 0,
    gst_percent: 18,
    prices_terms: 'EX-WORKS',
    payment_terms: 'AGAINST PI',
    dispatch_terms: '10-12 DAYS',
    freight_terms: 'AT YOUR COST',
    validity_terms: '30 DAYS',
    insurance_terms: 'AT YOUR COST',
    remarks: '',
  });

  const [items, setItems] = useState<LineItemInput[]>([]);
  const [currentItem, setCurrentItem] = useState<LineItemInput>({
    sno: 1,
    item_code: '',
    description: '',
    quantity: 1,
    rate: 0,
    discount_percent: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const [qRes, pRes, rRes, iRes] = await Promise.all([
      supabase.from('quotations').select('*').order('quotation_date', { ascending: false }),
      supabase.from('parties').select('*').order('name'),
      supabase.from('sales_reps').select('*').order('name'),
      supabase.from('item_codes').select('*').order('code'),
    ]);
    setQuotations(qRes.data || []);
    setParties(pRes.data || []);
    setReps(rRes.data || []);
    setCatalogItems(iRes.data || []);
    setLoading(false);
  }

  function computeNextQuotationNumber(companyIdVal: string | number, repIdVal: string | number): string {
    if (!companyIdVal || !repIdVal) return '';
    const comp = companies.find((c) => String(c.id) === String(companyIdVal));
    const rep = reps.find((r) => String(r.id) === String(repIdVal));
    if (!comp || !rep) return '';

    const currentYear = new Date().getFullYear();
    const prefix = `${comp.code}/${rep.code}/`;
    let maxSeq = 1000;

    quotations.forEach((q) => {
      if (q.quotation_number && q.quotation_number.startsWith(prefix)) {
        const parts = q.quotation_number.split('/');
        const numPart = parts.find((p, idx) => idx >= 2 && /^\d{3,5}$/.test(p));
        if (numPart) {
          const parsed = parseInt(numPart, 10);
          if (parsed > maxSeq) maxSeq = parsed;
        }
      }
    });

    return `${comp.code}/${rep.code}/${maxSeq + 1}/${currentYear}`;
  }

  function handleCompanyChange(newCompanyId: string) {
    const nextNum = computeNextQuotationNumber(newCompanyId, form.rep_id);
    setForm((prev) => ({
      ...prev,
      company_id: newCompanyId,
      quotation_number: nextNum || prev.quotation_number,
    }));
  }

  function handleRepChange(newRepId: string) {
    const nextNum = computeNextQuotationNumber(form.company_id, newRepId);
    setForm((prev) => ({
      ...prev,
      rep_id: newRepId,
      quotation_number: nextNum || prev.quotation_number,
    }));
  }

  function openNewQuotationModal() {
    setEditingId(null);
    const initialCompanyId = selectedCompanyId !== 'all' ? String(selectedCompanyId) : (companies[0]?.id ? String(companies[0].id) : '');
    const initialRepId = reps[0]?.id ? String(reps[0].id) : '';
    const initialNumber = computeNextQuotationNumber(initialCompanyId, initialRepId);

    setForm({
      quotation_number: initialNumber,
      quotation_date: new Date().toISOString().split('T')[0],
      company_id: initialCompanyId,
      rep_id: initialRepId,
      party_id: '',
      query_number: '',
      query_date: '',
      kind_attn: '',
      status: 'draft',
      global_discount_percent: 0,
      pf_percent: 0,
      others: 0,
      gst_percent: 18,
      prices_terms: 'EX-WORKS',
      payment_terms: 'AGAINST PI',
      dispatch_terms: '10-12 DAYS',
      freight_terms: 'AT YOUR COST',
      validity_terms: '30 DAYS',
      insurance_terms: 'AT YOUR COST',
      remarks: '',
    });
    setItems([]);
    setShowForm(true);
  }

  function handleProductSelect(itemCodeValue: string) {
    const found = catalogItems.find(i => i.code === itemCodeValue);
    if (found) {
      setCurrentItem({
        ...currentItem,
        item_code: found.code,
        description: found.description || found.code,
        rate: found.base_price || currentItem.rate || 0,
      });
    } else {
      setCurrentItem({ ...currentItem, item_code: itemCodeValue });
    }
  }

  function addItem() {
    if (!currentItem.item_code) return;
    setItems([...items, { ...currentItem, sno: items.length + 1 }]);
    setCurrentItem({
      sno: items.length + 2,
      item_code: '',
      description: '',
      quantity: 1,
      rate: 0,
      discount_percent: 0,
    });
  }

  function removeItem(index: number) {
    const updated = items.filter((_, i) => i !== index).map((it, idx) => ({ ...it, sno: idx + 1 }));
    setItems(updated);
  }

  // Calculate live financial summary using ported VBA pricing engine
  const financials = calculateFinancials(
    items,
    form.global_discount_percent,
    form.pf_percent,
    form.others,
    form.gst_percent
  );

  async function saveQuotation(e: FormEvent) {
    e.preventDefault();
    if (!form.company_id || !form.rep_id || !form.party_id) {
      alert('Please select Company, Sales Rep, and Party.');
      return;
    }

    const cleanQueryDate = form.query_date && form.query_date.length === 10 && !form.query_date.startsWith('000')
      ? form.query_date
      : null;

    const quotationData = {
      quotation_number: form.quotation_number,
      quotation_date: form.quotation_date,
      company_id: parseInt(form.company_id),
      rep_id: parseInt(form.rep_id),
      party_id: parseInt(form.party_id),
      query_number: form.query_number?.trim() || null,
      query_date: cleanQueryDate,
      kind_attn: form.kind_attn?.trim() || null,
      subtotal: financials.subtotal,
      discount_percent: financials.discountPercent,
      discount_amount: financials.discountAmount,
      pf_percent: financials.pfPercent,
      pf_amount: financials.pfAmount,
      others: financials.others,
      gst_percent: financials.gstPercent,
      gst_amount: financials.gstAmount,
      total: financials.total,
      amount_in_words: financials.amountInWords,
      prices_terms: form.prices_terms,
      payment_terms: form.payment_terms,
      dispatch_terms: form.dispatch_terms,
      freight_terms: form.freight_terms,
      validity_terms: form.validity_terms,
      insurance_terms: form.insurance_terms,
      remarks: form.remarks || financials.remarks || null,
      status: form.status,
    };

    const formatDbItem = (it: LineItemInput, quoteId: number, idx: number) => {
      const calc = calculateLineItem(it);
      return {
        quotation_id: quoteId,
        sno: it.sno ?? (idx + 1),
        item_code: it.item_code,
        description: it.description || '',
        quantity: Number(it.quantity) || 1,
        unit: (it as any).unit || 'Pcs',
        rate: Number(it.rate) || 0,
        amount: Number(calc.amount) || 0,
        discount_percent: Number(calc.discount_percent) || 0,
        discount_amount: Number(calc.discount_amount) || 0,
        final_amount: Number(calc.final_amount) || 0,
      };
    };

    if (editingId) {
      const { error: updateErr } = await supabase.from('quotations').update(quotationData).eq('id', editingId);
      if (updateErr) {
        alert('Failed to update quotation: ' + updateErr.message);
        return;
      }
      if (items.length > 0) {
        await supabase.from('quotation_items').delete().eq('quotation_id', editingId);
        const processed = items.map((it, idx) => formatDbItem(it, editingId, idx));
        const { error: itemErr } = await supabase.from('quotation_items').insert(processed);
        if (itemErr) {
          alert('Warning: Quotation updated, but line items could not be saved: ' + itemErr.message);
          return;
        }
      }
    } else {
      const { data, error: insertErr } = await supabase.from('quotations').insert(quotationData).select('id').single();
      if (insertErr || !data) {
        alert('Failed to create quotation: ' + (insertErr?.message || 'Database error'));
        return;
      }
      if (items.length > 0) {
        const processed = items.map((it, idx) => formatDbItem(it, data.id, idx));
        const { error: itemErr } = await supabase.from('quotation_items').insert(processed);
        if (itemErr) {
          alert('Warning: Quotation header created, but line items failed to save: ' + itemErr.message);
          return;
        }
      }
      logUserActivity({
        action_type: 'QUOTATION_CREATED',
        resource_id: quotationData.quotation_number,
        details: `Created quotation ${quotationData.quotation_number} (₹${quotationData.total})`,
      });
    }

    resetForm();
    loadData();
  }

  function resetForm() {
    setForm({
      quotation_number: '',
      quotation_date: new Date().toISOString().split('T')[0],
      company_id: '',
      rep_id: '',
      party_id: '',
      query_number: '',
      query_date: '',
      kind_attn: '',
      status: 'draft',
      global_discount_percent: 0,
      pf_percent: 0,
      others: 0,
      gst_percent: 18,
      prices_terms: 'EX-WORKS',
      payment_terms: 'AGAINST PI',
      dispatch_terms: '10-12 DAYS',
      freight_terms: 'AT YOUR COST',
      validity_terms: '30 DAYS',
      insurance_terms: 'AT YOUR COST',
      remarks: '',
    });
    setItems([]);
    setEditingId(null);
    setShowForm(false);
  }

  function editQuotation(q: Quotation) {
    if (onEditQuotation && q.id) {
      onEditQuotation(q.id);
    }
  }

  async function downloadPdf(q: Quotation) {
    const [itemsRes] = await Promise.all([
      supabase.from('quotation_items').select('*').eq('quotation_id', q.id).order('sno'),
    ]);

    const company = companies.find(c => c.id === q.company_id) || companies[0];
    const party = parties.find(p => p.id === q.party_id) || parties[0];
    const rep = reps.find(r => r.id === q.rep_id);

    if (!company || !party) return;

    const pdf = generateQuotationPDF(
      q,
      company,
      party,
      itemsRes.data || [],
      rep?.name
    );
    pdf.save(`${q.quotation_number.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    logUserActivity({
      action_type: 'PDF_GENERATED',
      resource_id: q.quotation_number,
      details: `Downloaded PDF for quotation ${q.quotation_number}`,
    });
  }

  async function downloadExcel(q: Quotation) {
    const [itemsRes] = await Promise.all([
      supabase.from('quotation_items').select('*').eq('quotation_id', q.id).order('sno'),
    ]);

    const company = companies.find(c => c.id === q.company_id) || companies[0];
    const party = parties.find(p => p.id === q.party_id) || parties[0];
    const rep = reps.find(r => r.id === q.rep_id);

    if (!company || !party) return;

    await generateQuotationExcel(
      q,
      company,
      party,
      itemsRes.data || [],
      rep?.name
    );
    logUserActivity({
      action_type: 'EXCEL_GENERATED',
      resource_id: q.quotation_number,
      details: `Downloaded Excel for quotation ${q.quotation_number}`,
    });
  }

  const filteredQuotations = quotations.filter((q) => {
    if (selectedCompanyId !== 'all' && q.company_id !== selectedCompanyId) {
      return false;
    }
    const query = searchQuery.toLowerCase();
    const party = parties.find((p) => p.id === q.party_id);
    const comp = companies.find((c) => c.id === q.company_id);
    return (
      q.quotation_number.toLowerCase().includes(query) ||
      (party && party.name.toLowerCase().includes(query)) ||
      (comp && comp.name.toLowerCase().includes(query)) ||
      (q.status && q.status.toLowerCase().includes(query))
    );
  });

  return (
    <div className="space-y-6">
      {/* Active Branch Filter Banner */}
      {selectedCompany && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="font-bold text-blue-900">Branch Filter Active:</span>
            <span className="px-2 py-0.5 rounded bg-blue-600 text-white font-mono font-bold">
              {selectedCompany.code}
            </span>
            <span className="text-blue-800 font-medium">
              {selectedCompany.name} ({filteredQuotations.length} of {quotations.length} total quotations)
            </span>
          </div>
          <button
            onClick={() => setSelectedCompanyId('all')}
            className="font-bold text-blue-700 hover:text-blue-900 underline cursor-pointer"
          >
            Show All Branches
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quotations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create, calculate with VBA pricing engine, and download quotations ({filteredQuotations.length} shown)
          </p>
        </div>
        <Button onClick={() => (showForm ? setShowForm(false) : openNewQuotationModal())}>
          {showForm ? 'Cancel' : '+ New Quotation'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={saveQuotation} className="bg-white shadow rounded-lg p-6 space-y-6 border border-gray-200">
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-lg font-bold text-gray-900">
              {editingId ? 'Edit Quotation' : 'Create New Quotation'}
            </h2>
            <span className="text-xs text-gray-500 bg-blue-50 text-blue-700 px-2 py-1 rounded">
              VBA RoundUp Engine Active
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">Quotation Number *</label>
                <button
                  type="button"
                  onClick={() => {
                    const autoNum = computeNextQuotationNumber(form.company_id, form.rep_id);
                    if (autoNum) setForm((prev) => ({ ...prev, quotation_number: autoNum }));
                  }}
                  className="text-[11px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer"
                  title="Recalculate next sequential number for selected branch and sales rep"
                >
                  <span>⚡ Auto-Generate</span>
                </button>
              </div>
              <input
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono font-bold bg-slate-50 focus:bg-white"
                value={form.quotation_number}
                onChange={(e) => setForm({ ...form, quotation_number: e.target.value.toUpperCase() })}
                placeholder="e.g. SKI/AB/1012/2026"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quotation Date *</label>
              <input
                type="date"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                value={form.quotation_date}
                onChange={(e) => setForm({ ...form, quotation_date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Operating Company *</label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-bold text-slate-800"
                value={form.company_id}
                onChange={(e) => handleCompanyChange(e.target.value)}
                required
              >
                <option value="">Select Company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} - {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Representative *</label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-bold text-slate-800"
                value={form.rep_id}
                onChange={(e) => handleRepChange(e.target.value)}
                required
              >
                <option value="">Select Rep</option>
                {reps.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} - {r.name} ({r.role || 'Sales'})
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Party (Customer) *</label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                value={form.party_id}
                onChange={(e) => setForm({ ...form, party_id: e.target.value })}
                required
              >
                <option value="">Select Customer</option>
                {parties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.address3 ? `(${p.address3})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Query Number</label>
              <input
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                value={form.query_number}
                onChange={(e) => setForm({ ...form, query_number: e.target.value })}
                placeholder="e.g. QRY-2026-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Query Date</label>
              <input
                type="date"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                value={form.query_date}
                onChange={(e) => setForm({ ...form, query_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as Quotation['status'] })}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Line Items Entry */}
          <div className="border-t pt-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-md font-bold text-gray-900">Line Items</h3>
              <span className="text-xs text-gray-500">Add products to quotation</span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="sm:col-span-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Item Code</label>
                <input
                  type="text"
                  aria-label="Line Item Code"
                  list="item-code-options"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  value={currentItem.item_code}
                  onChange={(e) => handleProductSelect(e.target.value)}
                  placeholder="e.g. SF1/SW/PP/F/615..."
                />
                <datalist id="item-code-options">
                  {catalogItems.slice(0, 50).map((it) => (
                    <option key={it.id} value={it.code}>
                      {it.description}
                    </option>
                  ))}
                </datalist>
              </div>
              <div className="sm:col-span-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                <input
                  aria-label="Line Item Description"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  value={currentItem.description}
                  onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                  placeholder="Assembly / product description"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Qty</label>
                <input
                  type="number"
                  aria-label="Line Item Quantity"
                  min="1"
                  className="block w-full px-2 py-2 border border-gray-300 rounded-md text-sm text-center"
                  value={currentItem.quantity}
                  onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Rate (₹)</label>
                <input
                  type="number"
                  aria-label="Line Item Rate"
                  step="0.01"
                  className="block w-full px-2 py-2 border border-gray-300 rounded-md text-sm text-right"
                  value={currentItem.rate}
                  onChange={(e) => setCurrentItem({ ...currentItem, rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Disc %</label>
                <input
                  type="number"
                  aria-label="Line Item Discount"
                  step="0.01"
                  className="block w-full px-2 py-2 border border-gray-300 rounded-md text-sm text-center"
                  value={currentItem.discount_percent}
                  onChange={(e) => setCurrentItem({ ...currentItem, discount_percent: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="sm:col-span-1 flex items-end">
                <button
                  type="button"
                  aria-label="Add Line Item"
                  onClick={addItem}
                  disabled={!currentItem.item_code}
                  className="w-full bg-primary-600 text-white px-3 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm font-medium"
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Table of added items */}
            {items.length > 0 && (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-bold text-gray-600">S.No.</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-600">Item Code & Description</th>
                      <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">Qty</th>
                      <th className="px-3 py-2 text-right text-xs font-bold text-gray-600">Rate (₹)</th>
                      <th className="px-3 py-2 text-right text-xs font-bold text-gray-600">Amount (₹)</th>
                      <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">Disc %</th>
                      <th className="px-3 py-2 text-right text-xs font-bold text-gray-600">Final (₹)</th>
                      <th className="px-3 py-2 text-center text-xs font-bold text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 text-sm">
                    {items.map((it, idx) => {
                      const calc = calculateLineItem(it);
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-500 text-center">{it.sno}</td>
                          <td className="px-4 py-2">
                            <span className="font-mono font-semibold text-gray-900">{it.item_code}</span>
                            {it.description && <p className="text-xs text-gray-500 mt-0.5">{it.description}</p>}
                          </td>
                          <td className="px-3 py-2 text-center">{it.quantity} Pcs</td>
                          <td className="px-3 py-2 text-right">₹{it.rate.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">₹{calc.amount.toFixed(2)}</td>
                          <td className="px-3 py-2 text-center text-blue-600">{it.discount_percent ? `${it.discount_percent}%` : '-'}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-900">₹{calc.final_amount.toFixed(2)}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => removeItem(idx)}
                              className="text-red-500 hover:text-red-700 font-bold"
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Financial Calculation Panel */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 border-t pt-4">
            <div className="space-y-3">
              <h3 className="text-md font-bold text-gray-900">Terms & Charges</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Global Discount %</label>
                  <input
                    type="number"
                    step="0.01"
                    className="mt-1 block w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                    value={form.global_discount_percent}
                    onChange={(e) => setForm({ ...form, global_discount_percent: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">P & F % (on Subtotal)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="mt-1 block w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                    value={form.pf_percent}
                    onChange={(e) => setForm({ ...form, pf_percent: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Other Charges (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="mt-1 block w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                    value={form.others}
                    onChange={(e) => setForm({ ...form, others: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">GST %</label>
                  <input
                    type="number"
                    step="0.01"
                    className="mt-1 block w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                    value={form.gst_percent}
                    onChange={(e) => setForm({ ...form, gst_percent: parseFloat(e.target.value) || 18 })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Remarks</label>
                <input
                  className="mt-1 block w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                  value={form.remarks || financials.remarks || ''}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  placeholder={financials.remarks || 'Terms / Remarks'}
                />
              </div>
            </div>

            {/* Live Financial Summary (VBA RoundUp Output) */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-2 text-sm">
              <h3 className="text-md font-bold text-gray-900 border-b pb-2">Financial Summary (VBA Compliant)</h3>
              <div className="flex justify-between text-gray-600">
                <span>SUBTOTAL:</span>
                <span className="font-semibold text-gray-900">₹{financials.subtotal.toLocaleString('en-IN')}</span>
              </div>
              {financials.discountAmount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount ({financials.discountPercent.toFixed(2)}%):</span>
                  <span>- ₹{financials.discountAmount.toLocaleString('en-IN')}</span>
                </div>
              )}
              {financials.pfAmount > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>P & F ({financials.pfPercent}% on Subtotal):</span>
                  <span>+ ₹{financials.pfAmount.toLocaleString('en-IN')}</span>
                </div>
              )}
              {financials.others > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Others:</span>
                  <span>+ ₹{financials.others.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600 border-t pt-1">
                <span>Taxable Base:</span>
                <span className="font-medium text-gray-900">₹{financials.taxableBase.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>GST ({financials.gstPercent}%):</span>
                <span>+ ₹{financials.gstAmount.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-primary-700 border-t-2 pt-2">
                <span>TOTAL:</span>
                <span>₹{financials.total.toLocaleString('en-IN')}</span>
              </div>
              <p className="text-xs text-gray-500 italic mt-1">{financials.amountInWords}</p>
            </div>
          </div>

          <div className="flex gap-2 justify-end border-t pt-4">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={items.length === 0}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-600/20 disabled:opacity-40 transition-all cursor-pointer"
            >
              {editingId ? 'Update Quotation' : 'Save Quotation'}
            </button>
          </div>
        </form>
      )}

      {/* Quotations List Container */}
      <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-200/80 bg-slate-50/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="w-full sm:w-96 relative">
            <input
              type="text"
              placeholder="Search by quote reference, party name, company, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-xs sm:text-sm bg-white border border-slate-300 rounded-xl focus:border-blue-500 shadow-sm text-slate-900 placeholder-slate-400 transition-colors"
            />
            <span className="absolute left-3.5 top-3 text-slate-400 text-sm">🔍</span>
          </div>
          <div className="text-xs font-semibold text-slate-500">
            Total Quotations: <span className="text-slate-900 font-bold">{filteredQuotations.length}</span>
          </div>
        </div>

        {loading ? (
          <div className="p-16 text-center text-slate-500 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto"></div>
            <span className="text-xs font-semibold">Loading quotations database...</span>
          </div>
        ) : filteredQuotations.length === 0 ? (
          <div className="p-16 text-center text-slate-400 space-y-2">
            <span className="text-3xl">📄</span>
            <p className="text-sm font-bold text-slate-700">No quotations found matching your search</p>
            <p className="text-xs text-slate-500">Try searching by client name or quotation number.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs divide-y divide-slate-200">
              <thead className="bg-slate-50/80 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-5 py-3.5">Quotation Ref</th>
                  <th className="px-4 py-3.5">Date</th>
                  <th className="px-4 py-3.5">Customer Party</th>
                  <th className="px-4 py-3.5">Entity</th>
                  <th className="px-4 py-3.5">Sales Rep</th>
                  <th className="px-4 py-3.5 text-right">Grand Total</th>
                  <th className="px-4 py-3.5 text-center">Status</th>
                  <th className="px-5 py-3.5 text-right">Exports & Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredQuotations.map((q) => {
                  const party = parties.find((p) => p.id === q.party_id);
                  const company = companies.find((c) => c.id === q.company_id);
                  const rep = reps.find((r) => r.id === q.rep_id);
                  return (
                    <tr key={q.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="px-5 py-3.5 font-mono font-bold text-slate-900 text-xs">
                        <div>{q.quotation_number}</div>
                        {q.query_number && (
                          <div className="text-[10px] font-sans font-normal text-slate-400 mt-0.5">
                            Ref: {q.query_number}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500 font-medium">
                        <div>{q.quotation_date}</div>
                        {q.query_date && (
                          <div className="text-[10px] font-semibold text-blue-600 mt-0.5 flex items-center gap-1">
                            <span>Qry:</span> <span>{q.query_date}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 font-bold text-slate-800 max-w-[200px] truncate">
                        {party?.name || 'Customer'}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                          {company?.code || 'SKI'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-slate-600 font-medium">
                        {rep?.name || rep?.code || '-'}
                      </td>
                      <td className="px-4 py-3.5 text-right font-extrabold text-slate-900 text-xs">
                        ₹{(q.total || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span
                          className={`px-2 py-0.5 text-[10px] rounded-full font-bold uppercase border ${
                            q.status === 'approved'
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                              : q.status === 'sent'
                              ? 'bg-blue-50 text-blue-800 border-blue-200'
                              : 'bg-slate-100 text-slate-700 border-slate-200'
                          }`}
                        >
                          {q.status || 'draft'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right whitespace-nowrap space-x-1.5">
                        <button
                          onClick={() => downloadPdf(q)}
                          className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold rounded-lg text-xs transition-colors"
                          title="Download official PDF document"
                        >
                          PDF 📄
                        </button>
                        <button
                          onClick={() => downloadExcel(q)}
                          className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-lg text-xs transition-colors"
                          title="Download synchronized Excel spreadsheet"
                        >
                          Excel 📊
                        </button>
                        <button
                          onClick={() => editQuotation(q)}
                          className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-xs transition-colors"
                          title="Edit Quotation"
                        >
                          Edit ✏️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
